import {
  Alert,
  Avatar,
  Box,
  Button,
  Container,
  Divider,
  Group,
  Loader,
  Paper,
  PasswordInput,
  Stack,
  Tabs,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";

import { IconDeviceFloppy, IconLock, IconUser } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { authClient } from "~/lib/auth-client";
import { errorNotification, successNotification } from "~/lib/notifications";

export const Route = createFileRoute("/_authenticated/profile")({
  component: ProfilePage,
});

function ProfilePage() {
  const session = authClient.useSession();

  if (session.isPending) {
    return (
      <Container size="md" py="xl">
        <Loader />
      </Container>
    );
  }

  if (!session.data?.user) {
    return (
      <Container size="md" py="xl">
        <Alert color="red" title="Authentication Error">
          You must be logged in to view this page.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="md" py="xl">
      <Stack gap="lg">
        <Title order={2}>Profile & Account</Title>

        <Tabs defaultValue="profile" keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="profile" leftSection={<IconUser size={16} />}>
              Profile Details
            </Tabs.Tab>
            <Tabs.Tab value="account" leftSection={<IconLock size={16} />}>
              Account Security
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="profile" pt="md">
            <ProfileDetails user={session.data.user} />
          </Tabs.Panel>

          <Tabs.Panel value="account" pt="md">
            <AccountSecurity />
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}

function ProfileDetails({ user }: { user: any }) {
  const queryClient = useQueryClient();

  const form = useForm({
    initialValues: {
      name: user.name || "",
      email: user.email || "",
    },
    validate: {
      name: (value) => (value.length < 2 ? "Name must be at least 2 characters" : null),
    },
  });

  // Update form if user prop changes safely
  useEffect(() => {
    if (user) {
      form.setInitialValues({
        name: user.name || "",
        email: user.email || "",
      });
      form.reset();
    }
  }, [user.name, user.email]); // Depend on specific fields to avoid loops if object ref changes

  const updateProfileMutation = useMutation({
    mutationFn: async (values: { name: string }) => {
      const response = await authClient.updateUser({
        name: values.name,
      });

      // better-auth returns { error, data } instead of throwing errors
      if (response.error) {
        throw new Error(response.error.message || "Failed to update profile");
      }

      return response.data;
    },
    onSuccess: () => {
      successNotification("Profile updated successfully");
      // Invalidate and refetch session data
      queryClient.invalidateQueries({ queryKey: ["session"] });
    },
    onError: (error: any) => {
      errorNotification(error.message || "Failed to update profile");
    },
  });

  const handleSubmit = (values: typeof form.values) => {
    updateProfileMutation.mutate({ name: values.name });
  };

  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="md">
        <Group>
          <Avatar src={user.image} size={80} radius="xl" name={user.name} />
          <Box>
            <Text size="lg" fw={500}>
              {user.name}
            </Text>
            <Text c="dimmed" size="sm">
              {user.email}
            </Text>
          </Box>
        </Group>

        <Divider />

        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            <TextInput label="Full Name" placeholder="Your name" {...form.getInputProps("name")} />
            <TextInput
              label="Email"
              description="Email cannot be changed directly."
              readOnly
              disabled
              {...form.getInputProps("email")}
            />

            <Group justify="flex-end">
              <Button type="submit" leftSection={<IconDeviceFloppy size={16} />} loading={updateProfileMutation.isPending}>
                Save Changes
              </Button>
            </Group>
          </Stack>
        </form>
      </Stack>
    </Paper>
  );
}

function AccountSecurity() {
  return (
    <Stack gap="md">
      <Paper withBorder p="md" radius="md">
        <Title order={4} mb="md">
          Change Password
        </Title>
        <ChangePasswordForm />
      </Paper>
    </Stack>
  );
}

function ChangePasswordForm() {
  const queryClient = useQueryClient();

  const form = useForm({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validate: {
      currentPassword: (val) => (val.length < 1 ? "Current password is required" : null),
      newPassword: (val) => (val.length < 8 ? "Password must be at least 8 characters" : null),
      confirmPassword: (val, values) => (val !== values.newPassword ? "Passwords do not match" : null),
    },
  });

  const changePasswordMutation = useMutation({
    mutationFn: async (values: { currentPassword: string; newPassword: string }) => {
      const response = await authClient.changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
        revokeOtherSessions: true,
      });

      // better-auth returns { error, data } instead of throwing errors
      if (response.error) {
        throw new Error(response.error.message || "Failed to change password");
      }

      return response.data;
    },
    onSuccess: () => {
      successNotification("Password changed successfully");
      form.reset();
      // Invalidate session data
      queryClient.invalidateQueries({ queryKey: ["session"] });
    },
    onError: (error: any) => {
      errorNotification(error.message || "Failed to change password");
    },
  });

  const handleChangePassword = (values: typeof form.values) => {
    changePasswordMutation.mutate({
      currentPassword: values.currentPassword,
      newPassword: values.newPassword,
    });
  };

  return (
    <form onSubmit={form.onSubmit(handleChangePassword)}>
      <Stack gap="md">
        <PasswordInput
          label="Current Password"
          placeholder="Enter current password"
          {...form.getInputProps("currentPassword")}
        />
        <PasswordInput label="New Password" placeholder="Enter new password" {...form.getInputProps("newPassword")} />
        <PasswordInput
          label="Confirm New Password"
          placeholder="Confirm new password"
          {...form.getInputProps("confirmPassword")}
        />
        <Group justify="flex-end">
          <Button type="submit" loading={changePasswordMutation.isPending}>
            Update Password
          </Button>
        </Group>
      </Stack>
    </form>
  );
}


